import DeviceContainer from '@/components/DeviceContainer';
import { ProductContext } from '@/context/productContext';
import { useContext, useEffect, useState } from 'react';
import { MyDevicesProps, getPlanType } from './utils';
import Button from '@/shared/Button';
import { ServiceContext } from '@/context/serviceContext';
import { ServiceRequestContext } from '@/context/serviceRequestContext';
import { tabItemNames } from '../../TabContainer/tabItems';
import { AvailablePlans, UpcomingPlanType } from './availablePlans';
import { fetchBenefits } from '@/actions/fetchBenefits';
import ProductUtils from '@/utils/productUtils';
import { BenefitsType } from '@/types/tier';
import { Benefits } from '@/mocks/amc/benefits';
import { AppScreens, WebEngageEventNames } from '@/types/wegengage';
import { WebEngageContext } from '@/context/webengageContext';
import WebEngageUtils from '@/utils/webengageUtils';
import { MY_SERVICES_TAB_URL } from '@/configs/constants';
import { disableScroll, enableScroll } from '@/components/DeviceContainer/deviceSelectionModal';
import FixedWrapper from '@/shared/FixedWrapper';

export const MyDevices = (props: MyDevicesProps) => {
  const { productsData, showBookServiceCta = true } = props;
  const { state: productState } = useContext(ProductContext);
  const { dispatch: serviceAction } = useContext(ServiceContext);
  const { dispatch: serviceRequestAction } = useContext(ServiceRequestContext);
  const { state: webengageState } = useContext(WebEngageContext);
  const { scriptLoaded } = webengageState;
  const { selectedProduct, amcServiceDetails } = productState;
  const { deviceId, currentPlan, id } = selectedProduct;
  const upcomingPlan = selectedProduct?.upcomingAMCPlan;
  const isUpcomingPlanAvailable = !!(upcomingPlan && upcomingPlan?.planType);
  const [upcomingPlanData, setUpcomingPlanData] = useState<UpcomingPlanType | null>(null);
  const [existingPlanData, setExistingPlanData] = useState<UpcomingPlanType | null>(null);
  const statusOfExistingPlanProduct = new ProductUtils(selectedProduct).getProductLabel();
  const isInContract =
    statusOfExistingPlanProduct !== 'In Warranty' &&
    statusOfExistingPlanProduct !== 'Warranty Expiring' &&
    statusOfExistingPlanProduct !== '';

  useEffect(() => {
    const getExistingPlanBenefits = async () => {
      if (selectedProduct && isInContract) {
        const tier =
          selectedProduct.planType === 'COMP' ? 'COMPREHENSIVE' : selectedProduct.planType;
        let active_benefits: BenefitsType[] = [];
        let active_price: number = 0;
        if (tier) {
          try {
            const { benefits, price } = await fetchBenefits(
              tier,
              deviceId,
              currentPlan?.agreementId,
              id,
            );
            active_benefits = benefits;
            active_price = price;
          } catch (error) {}
        }
        setExistingPlanData({
          planType: getPlanType(statusOfExistingPlanProduct, selectedProduct.planType),
          startDate: selectedProduct.currentPlan.startDate,
          endDate: selectedProduct.currentPlan.endDate,
          benefits: active_benefits,
          perYearPrice: active_price,
        });
      }
      if (selectedProduct && !isInContract) {
        setExistingPlanData({
          planType: getPlanType(statusOfExistingPlanProduct, selectedProduct.planType),
          startDate: selectedProduct.currentPlan.startDate,
          endDate: selectedProduct.currentPlan.endDate,
          benefits: Benefits.IN_WARRANTY.cartBenefits,
          perYearPrice: 0,
        });
      }
    };

    getExistingPlanBenefits();
  }, [selectedProduct]);

  useEffect(() => {
    const getUpcomingPlanBenefits = async () => {
      if (!!(selectedProduct && isUpcomingPlanAvailable)) {
        const tier = upcomingPlan.planType === 'COMP' ? 'COMPREHENSIVE' : upcomingPlan.planType;
        let new_benefits: BenefitsType[] = [];
        let new_price: number = 0;
        if (tier) {
          try {
            const { benefits, price } = await fetchBenefits(
              tier,
              deviceId,
              currentPlan?.agreementId,
              id,
            );
            new_benefits = benefits;
            new_price = price;
          } catch (error) {}
        }
        setUpcomingPlanData({
          planType: tier,
          startDate: upcomingPlan?.startDate,
          endDate: upcomingPlan?.endDate,
          benefits: new_benefits,
          perYearPrice: new_price,
        });
      }
      if (selectedProduct && !isUpcomingPlanAvailable) {
        setUpcomingPlanData(null);
      }
    };
    getUpcomingPlanBenefits();
  }, [selectedProduct]);

  useEffect(() => {
    const _we = window.webengage;
    if (_we && amcServiceDetails) {
      const { orderPricePerUnit, mandatoryServices } = amcServiceDetails;
      const { currentPlan } = selectedProduct;
      const { startDate = '', endDate = '' } = currentPlan;
      const _amcDuration = WebEngageUtils.calculatePlanDuration(startDate, endDate);
      const _serviceAvailed = WebEngageUtils.filterAvailed(mandatoryServices).length;
      const productUtils = new ProductUtils(selectedProduct);
      const webEngageObject = {
        navigated_from: AppScreens.DEVICE_DETAILS_SCREEN,
        customer_type: WebEngageUtils.stringUtil(selectedProduct?.expiryStatus?.status),
        product_selected: WebEngageUtils.stringUtil(selectedProduct?.name),
        amc_tier: WebEngageUtils.stringUtil(selectedProduct?.planType),
        amc_plan_duration: WebEngageUtils.stringUtil(_amcDuration),
        amc_plan_value: orderPricePerUnit ?? 0,
        amc_plan_valid_upto: WebEngageUtils.stringUtil(endDate),
        amc_plan_valid_from: WebEngageUtils.stringUtil(startDate),
        mandatory_service_available: mandatoryServices?.length ?? 0,
        mandatory_service_availed: _serviceAvailed,
        product_flag: productUtils.getProductLabel(),
        customer_code: WebEngageUtils.stringUtil(selectedProduct?.customerCode),
      };
      serviceRequestAction.initWebengageContext(webEngageObject);
      _we.track(WebEngageEventNames.DEVICE_DETAILS_SCREEN_OPENED, {
        ...webengageState.webengageDefault,
        ...webEngageObject,
      });
    }
  }, [scriptLoaded, amcServiceDetails]);

  useEffect(() => {
    if (productState.disablePageScroll) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [productState.disablePageScroll]);

  if (!selectedProduct) {
    return <></>;
  }

  const onClickBookAServiceButton = () => {
    const _we = window.webengage;
    if (_we) {
      _we.track(WebEngageEventNames.BOOK_A_SERVICE_CLICKED, {
        ...webengageState.webengageDefault,
        navigated_from: AppScreens.DEVICE_DETAILS_SCREEN,
        customer_type: WebEngageUtils.stringUtil(selectedProduct?.expiryStatus?.status),
        customer_code: WebEngageUtils.stringUtil(selectedProduct?.customerCode),
      });
    }

    serviceAction.setActiveTab(tabItemNames.ServiceRequests);
    serviceRequestAction.changeComponent('bookAService', tabItemNames.MyDevices);
  };

  const renderBookAServiceButton = () => {
    return (
      <Button
        className="w-full"
        onClick={onClickBookAServiceButton}
        dataTestId="book-a-service-bottom-button"
      >
        BOOK A SERVICE
      </Button>
    );
  };

  return (
    <section className="flex flex-col gap-s6">
      <DeviceContainer productsData={productsData} isServiceFlow />
      {/* {showBookServiceCta && (
        <div className="justify-center md:mb-6 hidden md:flex">
          <Button
            dataTestId="book-a-service-button"
            onClick={onClickBookAServiceButton}
            className={`md:w-[268px]`}
          >
            Book a Service
          </Button>
        </div>
      )} */}
      <AvailablePlans
        onClickBookAServiceButton={onClickBookAServiceButton}
        upcomingPlan={upcomingPlanData}
        existingPlan={existingPlanData}
      />
      {/* {showBookServiceCta && (
        <FixedWrapper className="p-6 pt-4 md:pb-4 flex gap-s5 md:hidden bg-white shadow-subtle z-10">
          {renderBookAServiceButton()}
        </FixedWrapper>
      )} */}
    </section>
  );
};
